import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { trackCreatorVisit } from "./lib/analytics";
import ProtectedRoute from "./components/ProtectedRoute";

import Login from "./pages/Login";
import Register from "./pages/Register";

// Demo pages
import VastavikDummy from "./pages/DemoDashboard";
import UploadDemo from "./pages/UpoadDemo";
import EmailVerification from "./pages/EmailVerification";

// Account / billing / support pages (parity with the mobile apps)
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import ChangePassword from "./pages/ChangePassword";
import DeleteAccount from "./pages/DeleteAccount";
import Storage from "./pages/Storage";
import Pricing from "./pages/Pricing";
import { HelpSupport, About, TermsPrivacy } from "./pages/StaticPages";
import { StorageGateGuard } from "./components/StorageGate";
import OfflineNotice from "./components/OfflineNotice";
import { useOnlineStatus } from "./hooks/useOnlineStatus";

const Guarded = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute>
    <StorageGateGuard>{children}</StorageGateGuard>
  </ProtectedRoute>
);

// Original pages (disabled for demo)
// import Dashboard from "./pages/Dashboard";
// import Upload from "./pages/Upload";

function App() {
  // Record a portal visit once per load (captures the ?src= platform tag).
  useEffect(() => { trackCreatorVisit(); }, []);

  const online = useOnlineStatus();

  return (
    <>
      {!online && <OfflineNotice />}
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<EmailVerification />} />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<Guarded><VastavikDummy /></Guarded>} />
          <Route path="/upload" element={<Guarded><UploadDemo /></Guarded>} />

          {/* Library lives inside the dashboard (folders/albums) */}
          <Route path="/library" element={<Guarded><VastavikDummy /></Guarded>} />

          {/* Account / billing / support */}
          <Route path="/profile" element={<Guarded><Profile /></Guarded>} />
          <Route path="/profile/edit" element={<Guarded><EditProfile /></Guarded>} />
          <Route path="/profile/password" element={<Guarded><ChangePassword /></Guarded>} />
          <Route path="/profile/delete" element={<Guarded><DeleteAccount /></Guarded>} />
          <Route path="/storage" element={<Guarded><Storage /></Guarded>} />
          <Route path="/pricing" element={<Guarded><Pricing /></Guarded>} />
          <Route path="/help" element={<Guarded><HelpSupport /></Guarded>} />
          <Route path="/about" element={<Guarded><About /></Guarded>} />
          <Route path="/terms" element={<Guarded><TermsPrivacy /></Guarded>} />

          {/* Disabled Protected Routes (for now) */}
          {/*
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/upload"
            element={
              <ProtectedRoute>
                <Upload />
              </ProtectedRoute>
            }
          />
          */}

          {/* Default route → Demo Dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
    </>
  );
}

export default App;
